import { css } from "@compiled/react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { richTextToHTML } from "src/clients/uniform-client/enhancers/utils";

import { media } from "@atlaskit/primitives/responsive";

import {
    templateGuideContentfulRelayFragment$data,
    templateGuideContentfulRelayFragment$key
} from "@/__generated_contentful__/templateGuideContentfulRelayFragment.graphql";
import { SanitizedRichText } from "@/components/confluence/util/sanitized-rich-text";
import { expectContent } from "@/content/utils/expectContent";

type TemplateGuideProps = {
    templateGuide: templateGuideContentfulRelayFragment$key;
};

interface TemplateDetailStepProps {
    step: NonNullable<
        templateGuideContentfulRelayFragment$data["templateStepsCollection"]
    >["items"][number];
}

const stepContainerStyle = css({
    "display": "flex",
    "justifyContent": "space-between",
    "paddingBlockEnd": "3rem",
    "marginBlockEnd": "3rem",
    "borderBlockEnd": "2px solid #ebecf0",
    "gap": "2rem",

    "@media (max-width: 39.99rem)": {
        flexDirection: "column"
    },

    ":last-of-type": {
        borderBlockEnd: "none"
    }
});

const guideSectionHeading = css({
    "fontSize": "2.25rem",
    "lineHeight": "2.75rem",
    "fontWeight": 500,
    "letterSpacing": 0.3,
    "color": "#1d3059",
    "fontFamily": "var(--bxp-charlie-display-font-family)",
    "margin": 0,
    "marginBlockEnd": 40,

    "@media (max-width: 47.99rem)": {
        fontSize: "1.5rem",
        lineHeight: "1.75rem"
    }
});

const stepHeading = css({
    fontSize: "1.5rem",
    lineHeight: "2rem",
    fontWeight: 500,
    letterSpacing: 0.3,
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#1d3059",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    fontFamily: "var(--bxp-charlie-display-font-family)",
    margin: 0,

    [media.below.sm]: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem"
    }
});

const stepContentWrapper = css({
    "display": "flex",
    "flexDirection": "column",
    "gap": "2rem",

    "@media (max-width: 39.99rem)": {
        gap: "1.5rem"
    }
});

const stepImageWrapper = css({
    "flexGrow": 0,
    "flexShrink": 0,
    "@media (max-width: 39.99rem)": {
        textAlign: "center"
    }
});

const stepImageStyle = css({
    "maxWidth": 328,

    "@media (min-width: 40rem) and (max-width: 63.99rem)": {
        maxWidth: 208
    },

    "@media (max-width: 39.99rem)": {
        maxWidth: 288
    }
});

const TemplateDetailStep = ({ step }: TemplateDetailStepProps) => {
    return (
        <div css={stepContainerStyle}>
            <div css={stepContentWrapper}>
                <h3 css={stepHeading}>{step?.stepNameExternal}</h3>
                {step?.stepDescription && (
                    <SanitizedRichText
                        innerHTML={richTextToHTML(step?.stepDescription.json)}
                    />
                )}
            </div>
            {step?.stepAsset && (
                <div css={stepImageWrapper}>
                    {/* eslint-disable-next-line no-restricted-syntax */}
                    <img
                        css={stepImageStyle}
                        src={step.stepAsset?.url || ""}
                        alt={step.stepNameExternal ?? ""}
                    />
                </div>
            )}
        </div>
    );
};

const TemplateGuideRelayFragment = graphql`
    fragment templateGuideContentfulRelayFragment on TemplateGuide {
        templateGuideNameExternal
        templateStepsCollection {
            items {
                stepNameExternal
                stepDescription {
                    json
                }
                stepAsset {
                    url
                }
            }
        }
    }
`;

export const TemplateGuide = ({ templateGuide }: TemplateGuideProps) => {
    const data = useFragment(TemplateGuideRelayFragment, templateGuide);
    const name = data.templateGuideNameExternal;
    const steps = data.templateStepsCollection?.items;

    const guideName = expectContent(name, "Template guide name not found");
    const guideSectionStyle = css({
        "marginBlockStart": "56px",
        "marginInlineStart": "auto",
        "marginInlineEnd": "auto",

        "@media(max-width: 768px)": {
            marginInlineEnd: "0",
            marginInlineStart: "0"
        },

        "@media (max-width: 39.99rem)": {
            maxWidth: "100%"
        },

        "@media (min-width: 40rem) and (max-width: 63.99rem)": {
            maxWidth: 688
        }
    });

    return (
        <section css={guideSectionStyle}>
            <h2 css={guideSectionHeading}>{guideName}</h2>
            {steps?.map((step, index) => (
                <TemplateDetailStep key={`step-${index}`} step={step} />
            ))}
        </section>
    );
};
